import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine';
import { Edit, Delete, Add, AttachMoney, Download, Upload, FileUpload } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { 
  getRatesByTransportLine,
  createRate,
  updateRate,
  deleteRate,
  getCities,
  getPopularCities,
  getAllCities,
  getAccounts
} from '../../api';

// Leaflet icon configuration
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const originIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const RouteLayer = ({ originCoords, destCoords, setRouteDistance }) => {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !originCoords || !destCoords) return;
    
    const routingControl = L.Routing.control({
      waypoints: [
        L.latLng(originCoords[0], originCoords[1]),
        L.latLng(destCoords[0], destCoords[1])
      ],
      routeWhileDragging: false,
      showAlternatives: false,
      show: false,
      lineOptions: {
        styles: [
          {color: '#0078ff', opacity: 0.8, weight: 4},
          {color: '#0000ff', opacity: 0.1, weight: 8}
        ],
        addWaypoints : false,
        
      },
      router: L.Routing.osrmv1({
        serviceUrl: 'https://router.project-osrm.org/route/v1',
        profile: 'driving'
      })
    }).addTo(map);

    routingControl.on('routesfound', function(e) {
      const routes = e.routes;
      const summary = routes[0].summary;
      const distanceInKm = Math.round(summary.totalDistance / 1000);
      setRouteDistance(distanceInKm);
      console.log('Route calculated, distance:', distanceInKm, 'km');
    });

    return () => {
      map.removeControl(routingControl);
    };
  }, [map, originCoords, destCoords, setRouteDistance]);

  return null;
};

const MapControlsComponent = ({ setMapInstance }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (map) {
      setMapInstance(map);
    }
  }, [map, setMapInstance]);
  
  return null;
};

const TarifariosManagement = ({ selectedLine }) => {
  const [tarifarios, setTarifarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTarifario, setEditingTarifario] = useState(null);
  const [formData, setFormData] = useState({
    origen: '',
    destino: '',
    monto: '',
    tipo_transporte: '',
    moneda: 'MXN',
    observaciones: '',
    cliente: ''  // Add cliente field
  });
  
  // Add new state variables for client selection
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [originInputValue, setOriginInputValue] = useState('');
  const [destinationInputValue, setDestinationInputValue] = useState('');
  const [originLoading, setOriginLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);
  
  const [originCoordinates, setOriginCoordinates] = useState(null);
  const [destinationCoordinates, setDestinationCoordinates] = useState(null);
  const [selectedOriginCity, setSelectedOriginCity] = useState(null);
  const [selectedDestinationCity, setSelectedDestinationCity] = useState(null);
  const [distance, setDistance] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);

  const [debugInfo, setDebugInfo] = useState({
    lastSearchQuery: '',
    cityResultCount: 0,
    error: null
  });

  const [loadingAllCities, setLoadingAllCities] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [processingFile, setProcessingFile] = useState(false);
  const [fileData, setFileData] = useState([]);

  const [validationResults, setValidationResults] = useState([]);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [validatedData, setValidatedData] = useState([]);

  useEffect(() => {
    if (selectedLine?.id) {
      fetchTarifarios();
    }
  }, [selectedLine]);

  useEffect(() => {
    const loadPopularCities = async () => {
      if (isModalOpen) {
        try {
          setOriginLoading(true);
          setDestinationLoading(true);

          const popularCities = await getPopularCities();
          
          if (popularCities.length > 0) {
            setOriginOptions(popularCities);
            setDestinationOptions(popularCities);
          }
        } catch (error) {
          console.error('Error loading popular cities:', error);
        } finally {
          setOriginLoading(false);
          setDestinationLoading(false);
        }
      }
    };

    loadPopularCities();
  }, [isModalOpen]);

  const fetchTarifarios = async () => {
    if (!selectedLine?.id) return;
    
    try {
      setLoading(true);
      const data = await getRatesByTransportLine(selectedLine.id);
      setTarifarios(data || []);
    } catch (error) {
      console.error('Error al cargar tarifarios:', error);
      toast.error('Error al cargar los tarifarios');
    } finally {
      setLoading(false);
    }
  };

  const searchCities = async (searchQuery, isOrigin) => {
    if (!searchQuery || searchQuery.length < 2) {
      return;
    }

    setDebugInfo(prev => ({ 
      ...prev, 
      lastSearchQuery: searchQuery
    }));

    try {
      if (isOrigin) {
        setOriginLoading(true);
      } else {
        setDestinationLoading(true);
      }
      
      const cities = await getCities(searchQuery);
      
      setDebugInfo(prev => ({ 
        ...prev, 
        cityResultCount: cities.length,
        error: null
      }));
      
      if (Array.isArray(cities) && cities.length > 0) {
        if (isOrigin) {
          setOriginOptions(cities);
        } else {
          setDestinationOptions(cities);
        }
      }
    } catch (error) {
      console.error('Error buscando ciudades:', error);
      setDebugInfo(prev => ({ 
        ...prev, 
        error: error.message || 'Error desconocido'
      }));
    } finally {
      if (isOrigin) {
        setOriginLoading(false);
      } else {
        setDestinationLoading(false);
      }
    }
  };

  const handleLoadAllCities = async () => {
    try {
      setLoadingAllCities(true);
      setOriginLoading(true);
      setDestinationLoading(true);
      
      const allCities = await getAllCities();
      if (allCities.length > 0) {
        setOriginOptions(allCities);
        setDestinationOptions(allCities);
        toast.success(`Cargadas ${allCities.length} ciudades`);
      } else {
        toast.error('No se pudieron cargar las ciudades');
      }
    } catch (error) {
      console.error('Error loading all cities:', error);
      toast.error('Error al cargar todas las ciudades');
    } finally {
      setLoadingAllCities(false);
      setOriginLoading(false);
      setDestinationLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (originInputValue) {
        searchCities(originInputValue, true);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [originInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (destinationInputValue) {
        searchCities(destinationInputValue, false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [destinationInputValue]);

  const handleOpenModal = async (tarifario = null) => {
    // Load client options when modal opens
    try {
      setClientLoading(true);
      const accounts = await getAccounts();
      setClientOptions(accounts || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setClientLoading(false);
    }
    
    if (tarifario) {
      setEditingTarifario(tarifario);
      setFormData({
        origen: tarifario.origen,
        destino: tarifario.destino,
        monto: tarifario.monto,
        tipo_transporte: tarifario.tipo_transporte,
        moneda: tarifario.moneda || 'MXN',
        observaciones: tarifario.observaciones || '',
        cliente: tarifario.cliente || '' // Include cliente field
      });
      setOriginInputValue(tarifario.origen);
      setDestinationInputValue(tarifario.destino);
      setSelectedClient(tarifario.cliente || null);

      // Load city data for origin and destination
      try {
        const originCities = await getCities(tarifario.origen);
        if (originCities && originCities.length > 0) {
          const originCity = originCities.find(city => city.nombre === tarifario.origen) || originCities[0];
          setOriginOptions(originCities);
          setSelectedOriginCity(originCity);
          setOriginCoordinates([originCity.latitud, originCity.longitud]);
          setTimeout(() => {
            const originInput = document.getElementById('origen-autocomplete');
            if (originInput) {
              const event = new Event('change', { bubbles: true });
              originInput.dispatchEvent(event);
            }
          }, 100);
        }

        const destCities = await getCities(tarifario.destino);
        if (destCities && destCities.length > 0) {
          const destCity = destCities.find(city => city.nombre === tarifario.destino) || destCities[0];
          setDestinationOptions(destCities);
          setSelectedDestinationCity(destCity);
          setDestinationCoordinates([destCity.latitud, destCity.longitud]);
          setTimeout(() => {
            const destInput = document.getElementById('destino-autocomplete');
            if (destInput) {
              const event = new Event('change', { bubbles: true });
              destInput.dispatchEvent(event);
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error loading cities for editing:', error);
        toast.error('Error al cargar las ciudades para edición');
      }
    } else {
      setEditingTarifario(null);
      setFormData({
        origen: '',
        destino: '',
        monto: '',
        tipo_transporte: '',
        moneda: 'MXN',
        observaciones: '',
        cliente: '' // Include cliente field
      });
      setOriginInputValue('');
      setDestinationInputValue('');
      setOriginCoordinates(null);
      setDestinationCoordinates(null);
      setSelectedOriginCity(null);
      setSelectedDestinationCity(null);
      setOriginOptions([]);
      setDestinationOptions([]);
      setDistance(null);
      setSelectedClient(null); // Reset selected client
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTarifario(null);
    setFormData({
      origen: '',
      destino: '',
      monto: '',
      tipo_transporte: '',
      moneda: 'MXN',
      observaciones: '',
      cliente: '' // Include cliente field
    });
    setOriginInputValue('');
    setDestinationInputValue('');
    setOriginCoordinates(null);
    setDestinationCoordinates(null);
    setDistance(null);
    setSelectedClient(null);
  };

  const handleSaveTarifario = async () => {
    if (!selectedLine?.id) {
      toast.error('Debe seleccionar una línea de transporte');
      return;
    }

    if (!formData.origen || !formData.destino || !formData.monto || !formData.tipo_transporte) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    try {
      const tarifarioData = {
        ...formData,
        linea_transporte_id: selectedLine.id,
        monto: parseFloat(formData.monto)
      };

      if (editingTarifario) {
        await updateRate(editingTarifario.id, tarifarioData);
        toast.success('Tarifario actualizado correctamente');
      } else {
        await createRate(tarifarioData);
        toast.success('Tarifario creado correctamente');
      }
      
      handleCloseModal();
      fetchTarifarios();
    } catch (error) {
      console.error('Error al guardar tarifario:', error);
      toast.error('Error al guardar el tarifario');
    }
  };

  const handleDeleteTarifario = async (id) => {
    try {
      await deleteRate(id);
      toast.success('Tarifario eliminado correctamente');
      fetchTarifarios();
    } catch (error) {
      console.error('Error al eliminar tarifario:', error);
      toast.error('Error al eliminar el tarifario');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleExportToExcel = () => {
    if (!tarifarios || tarifarios.length === 0) {
      toast.error('No hay tarifarios para exportar');
      return;
    }

    try {
      const exportData = tarifarios.map(item => ({
        Origen: item.origen,
        Destino: item.destino,
        Monto: item.monto,
        Moneda: item.moneda,
        'Tipo de Transporte': item.tipo_transporte,
        Cliente: item.cliente || '',
        Observaciones: item.observaciones || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const columnWidths = [
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
        { wch: 10 },
        { wch: 20 },
        { wch: 20 },
        { wch: 30 }
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tarifarios');

      const fileName = `tarifarios_${selectedLine?.name || 'linea'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success('Tarifarios exportados correctamente');
    } catch (error) {
      console.error('Error al exportar tarifarios:', error);
      toast.error('Error al exportar tarifarios');
    }
  };

  const handleSelectCity = (city, isOrigin) => {
    if (!city) return;
    
    if (isOrigin) {
      setSelectedOriginCity(city);
      setOriginCoordinates([city.latitud, city.longitud]);
      setFormData({
        ...formData,
        origen: city.nombre
      });
    } else {
      setSelectedDestinationCity(city);
      setDestinationCoordinates([city.latitud, city.longitud]);
      setFormData({
        ...formData,
        destino: city.nombre
      });
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet([{
        'Origen': '',
        'Destino': '',
        'Monto': '',
        'Tipo de Transporte': 'TRAILER',
        'Moneda': 'MXN',
        'Cliente': '', // Add Cliente field to template
        'Observaciones': ''
      }]);
      
      const columnWidths = [
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
        { wch: 20 },
        { wch: 10 },
        { wch: 15 }, // Width for Cliente column
        { wch: 30 }
      ];
      worksheet['!cols'] = columnWidths;
      
      
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
     
      worksheet.A3 = { t: 's', v: '' };
      worksheet.A4 = { t: 's', v: 'Instrucciones:' };
      worksheet.A5 = { t: 's', v: '1. En la hoja "Listas" encontrará los valores válidos para "Tipo de Transporte" y "Moneda"' };
      worksheet.A6 = { t: 's', v: '2. Se recomienda utilizar estos valores para evitar errores en la importación' };
      worksheet.A7 = { t: 's', v: '3. Complete la información y elimine estas filas de instrucciones antes de importar' };

      XLSX.writeFile(workbook, 'plantilla_tarifarios.xlsx');
      
      toast.success('Plantilla descargada correctamente');
    } catch (error) {
      console.error('Error al generar la plantilla:', error);
      toast.error('Error al generar la plantilla');
    }
  };

  const validateFileData = (data) => {
    if (!data || data.length === 0) return false;
    
    const firstRow = data[0];
    const requiredFields = ['Origen', 'Destino', 'Monto', 'Tipo de Transporte'];
    
    return requiredFields.every(field => Object.keys(firstRow).includes(field));
  };

  const procesarImportacion = async () => {
    try {
      setUploadError('');
      
      let successCount = 0;
      let errorCount = 0;
      
      const dataToImport = validatedData.length > 0 ? validatedData : fileData;
      
      for (const row of dataToImport) {
        try {
          const tarifarioData = {
            origen: row.Origen,
            destino: row.Destino,
            monto: parseFloat(row.Monto),
            tipo_transporte: row['Tipo de Transporte'],
            moneda: row.Moneda || 'MXN',
            cliente: row.Cliente || '', // Include cliente field
            observaciones: row.Observaciones || '',
            linea_transporte_id: selectedLine.id
          };
          
          await createRate(tarifarioData);
          successCount++;
        } catch (err) {
          console.error('Error al importar tarifario:', err);
          errorCount++;
        }
      }
      
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      setFileData([]);
      setValidatedData([]);
      setValidationResults([]);
      fetchTarifarios();
      
      toast.success(`Importación completada. Agregados: ${successCount}, Errores: ${errorCount}`);
    } catch (error) {
      console.error('Error durante la importación:', error);
      const errorMsg = 'Error durante el proceso de importación. Inténtelo de nuevo.';
      setUploadError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setProcessingFile(false);
    }
  };

  const columns = [
  { field: 'cliente', headerName: 'Cliente', width: 150 }, 
    { field: 'origen', headerName: 'Origen', flex: 1 },
    { field: 'destino', headerName: 'Destino', flex: 1 },
    { 
      field: 'monto', 
      headerName: 'Tarifa', 
      width: 120,
      renderCell: (params) => {
        const formattedValue = new Intl.NumberFormat('es-MX', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(params.value);
        
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            height: '100%',
            width: '100%',
            justifyContent: 'flex-start'
          }}>
            <AttachMoney fontSize="small" sx={{ color: 'primary.main', mr: 0.5 }} />
            <Typography variant="body2">
              {formattedValue} {params.row.moneda}
            </Typography>
          </Box>
        );
      }
    },
    { field: 'tipo_transporte', headerName: 'Tipo de Transporte', width: 180 },
    
    { field: 'observaciones', headerName: 'Observaciones', flex: 1 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton 
            size="small" 
            onClick={() => handleOpenModal(params.row)}
            sx={{ mr: 1 }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="error"
            onClick={() => handleDeleteTarifario(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const mapCenter = React.useMemo(() => {
    if (originCoordinates && destinationCoordinates) {
      return [
        (originCoordinates[0] + destinationCoordinates[0]) / 2,
        (originCoordinates[1] + destinationCoordinates[1]) / 2
      ];
    } else if (originCoordinates) {
      return originCoordinates;
    } else if (destinationCoordinates) {
      return destinationCoordinates;
    } else {
      return [19.4326, -99.1332];
    }
  }, [originCoordinates, destinationCoordinates]);

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Tarifarios de {selectedLine?.name || 'la línea seleccionada'}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<Download />}
            onClick={handleExportToExcel} 
            disabled={!selectedLine?.id || tarifarios.length === 0}
          >
            Descargar Tarifario
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<Upload />}
            onClick={() => setIsUploadModalOpen(true)} 
            disabled={!selectedLine?.id}
          >
            Carga Masiva
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => handleOpenModal()} 
            disabled={!selectedLine?.id}
          >
            Nuevo Tarifario
          </Button>
        </Box>
      </Box>
      
      <Paper elevation={2} sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={tarifarios}
          columns={columns}
          loading={loading}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          disableSelectionOnClick
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      <Dialog 
        open={isModalOpen} 
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingTarifario ? 'Editar Tarifario' : 'Nuevo Tarifario'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ width: '45%', height: '500px', border: '1px solid #e0e0e0', borderRadius: '4px', position: 'relative' }}>
              {routeDistance !== null && (
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: '10px', 
                  left: '10px', 
                  zIndex: 1000,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                  padding: '5px 10px', 
                  borderRadius: '4px',
                  border: '1px solid #0078ff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  fontWeight: 'bold'
                }}>
                  Distancia por carretera: {routeDistance} km
                </Box>
              )}
              <MapContainer 
                center={mapCenter} 
                zoom={5} 
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {originCoordinates && (
                  <Marker position={originCoordinates} icon={originIcon}>
                    <Popup>
                      <b>Origen:</b> {formData.origen}
                    </Popup>
                  </Marker>
                )}
                
                {destinationCoordinates && (
                  <Marker position={destinationCoordinates} icon={destinationIcon}>
                    <Popup>
                      <b>Destino:</b> {formData.destino}
                    </Popup>
                  </Marker>
                )}

                <MapControlsComponent setMapInstance={setMapRef} />
                
                {originCoordinates && destinationCoordinates && (
                  <RouteLayer 
                    originCoords={originCoordinates} 
                    destCoords={destinationCoordinates}
                    setRouteDistance={setRouteDistance}
                  />
                )}
              </MapContainer>
            </Box>

            <Box sx={{ width: '55%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLoadAllCities}
                  disabled={loadingAllCities}
                  startIcon={loadingAllCities ? <CircularProgress size={16} /> : null}
                >
                  {loadingAllCities ? 'Cargando...' : 'Cargar todas las ciudades'}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  id="origen-autocomplete"
                  options={originOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.nombre && option.estado 
                      ? `${option.nombre}, ${option.estado}` 
                      : '';
                  }}
                  onChange={(event, newValue) => handleSelectCity(newValue, true)}
                  onInputChange={(event, newValue) => {
                    setOriginInputValue(newValue);
                  }}
                  value={selectedOriginCity}
                  loading={originLoading}
                  sx={{ flex: 1 }}
                  noOptionsText="No se encontraron ciudades"
                  loadingText="Buscando ciudades..."
                  fullWidth
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || `ciudad-${option.nombre}-${option.estado}`}>
                      {option.nombre}, {option.estado}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Origen"
                      required
                      placeholder="Buscar ciudad..."
                      helperText={originOptions.length === 0 && !originLoading ? "Escriba para buscar" : ""}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {originLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  id="destino-autocomplete"
                  options={destinationOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.nombre && option.estado 
                      ? `${option.nombre}, ${option.estado}` 
                      : '';
                  }}
                  onChange={(event, newValue) => handleSelectCity(newValue, false)}
                  onInputChange={(event, newValue) => setDestinationInputValue(newValue)}
                  value={selectedDestinationCity}
                  loading={destinationLoading}
                  sx={{ flex: 1 }}
                  noOptionsText="No se encontraron ciudades"
                  loadingText="Buscando ciudades..."
                  fullWidth
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || `ciudad-${option.nombre}-${option.estado}`}>
                      {option.nombre}, {option.estado}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Destino"
                      required
                      placeholder="Buscar ciudad..."
                      helperText={destinationOptions.length === 0 && !destinationLoading ? "Escriba para buscar" : ""}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {destinationLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Add client autocomplete field */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  id="cliente-autocomplete"
                  options={clientOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.nombre_cuenta || ''; // Changed from option.name to option.nombre_cuenta
                  }}
                  onChange={(event, newValue) => {
                    setSelectedClient(newValue);
                    setFormData({
                      ...formData,
                      cliente: newValue ? newValue.nombre_cuenta || newValue : '' // Changed from newValue.name to newValue.nombre_cuenta
                    });
                  }}
                  value={selectedClient}
                  loading={clientLoading}
                  sx={{ flex: 1 }}
                  noOptionsText="No se encontraron clientes"
                  loadingText="Cargando clientes..."
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Seleccione un cliente..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {clientLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Monto"
                  name="monto"
                  type="number"
                  required
                  value={formData.monto}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ flex: 1 }}
                />
                
                <FormControl sx={{ flex: 0.5 }}>
                  <InputLabel>Moneda</InputLabel>
                  <Select
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleInputChange}
                    label="Moneda"
                  >
                    <MenuItem value="MXN">MXN</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Tipo de Transporte</InputLabel>
                <Select
                  name="tipo_transporte"
                  value={formData.tipo_transporte}
                  onChange={handleInputChange}
                  label="Tipo de Transporte"
                  required
                >
                  <MenuItem value="CAJA 53FT SECA">53FT</MenuItem>
                  <MenuItem value="CAJA 53FT REFRIGERADA">53FT THERMO</MenuItem>
                  <MenuItem value="CAJA 48FT SECA">48FT</MenuItem>
                  <MenuItem value="CAJA 40FT SECA">40FT </MenuItem>
                  <MenuItem value="CAJA 40FT REFRIGERADA">40FT THERMO</MenuItem>
                  <MenuItem value="PLATAFORMA 40FT">FLAT 40FT</MenuItem>
                  <MenuItem value="Full">FULL</MenuItem>
                  <MenuItem value="CHASIS 40FT">CHASIS 40FT</MenuItem>
                  <MenuItem value="CHASIS 20FT">CHASIS 20FT</MenuItem>
                  <MenuItem value="TORTON">TORTON</MenuItem>
                  <MenuItem value="RABON">RABON</MenuItem>
                  <MenuItem value="CAMIONETA 3.5 TON">CAMIONETA 3.5 TON</MenuItem>
                  <MenuItem value="CAMIONETA 1.5 TON">CAMIONETA 1.5 TON</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Observaciones"
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveTarifario}
            disabled={!formData.origen || !formData.destino || !formData.monto || !formData.tipo_transporte}
          >
            {editingTarifario ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Carga Masiva de Tarifarios</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Suba un archivo Excel con las siguientes columnas: Origen, Destino, Monto, Tipo de Transporte, Moneda (opcional, por defecto MXN), Cliente (opcional), Observaciones (opcional).
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<Download />}
                onClick={handleDownloadTemplate}
                sx={{ ml: 2, whiteSpace: 'nowrap' }}
              >
                Descargar Plantilla
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '2px dashed #ccc', borderRadius: 2, p: 3, mt: 2 }}>
              <FileUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Seleccione un archivo Excel
              </Typography>
              <Button
                variant="outlined"
                component="label"
                sx={{ mt: 2 }}
              >
                Examinar
                <input
                  type="file"
                  hidden
                  accept=".xlsx, .xls"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const data = new Uint8Array(event.target.result);
                        const workbook = XLSX.read(data, { type: 'array' });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);
                        if (validateFileData(jsonData)) {
                          setFileData(jsonData);
                          toast.success('Archivo cargado correctamente');
                        } else {
                          toast.error('El archivo no tiene el formato esperado');
                        }
                      };
                      reader.readAsArrayBuffer(file);
                    }
                  }}
                />
              </Button>
            </Box>

            {fileData.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {fileData.length} tarifarios encontrados en el archivo
                </Typography>
                <Paper sx={{ height: 200, width: '100%', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: 8, textAlign: 'left' }}>Origen</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Destino</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Monto</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Tipo de Transporte</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Cliente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fileData.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: 8 }}>{item.Origen}</td>
                          <td style={{ padding: 8 }}>{item.Destino}</td>
                          <td style={{ padding: 8 }}>{item.Monto}</td>
                          <td style={{ padding: 8 }}>{item['Tipo de Transporte']}</td>
                          <td style={{ padding: 8 }}>{item.Cliente || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUploadModalOpen(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={procesarImportacion}
            disabled={fileData.length === 0 || processingFile}
          >
            {processingFile ? 'Procesando...' : 'Importar Tarifarios'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TarifariosManagement;
